import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import {
  EvConnectorTypeOption,
  EvPriceRateRow,
} from '../../../../../core/models/system-settings/ev-price-rate.model';
import { ParkingGateDto } from '../../../../../core/models/system-settings/parking-master-data.model';
import { MapperEvPriceRateService } from '../../../../../core/mappers/system-settings/mapper-ev-price-rate.service';
import { MapperParkingMasterDataService } from '../../../../../core/mappers/system-settings/mapper-parking-master-data.service';
import { ApiEvPriceRateService } from '../../../../../core/services/api/system-settings/api-ev-price-rate.service';
import { ApiParkingMasterDataService } from '../../../../../core/services/api/system-settings/api-parking-master-data.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-ev-price-rate',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-ev-price-rate.component.html',
  styleUrl: './modal-ev-price-rate.component.css',
})
export class ModalEvPriceRateComponent implements OnInit {
  @Input() itemId = 0;
  @Input() siteId = 0;
  @Input() siteName = '';
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  formData!: EvPriceRateRow;
  connectors: EvConnectorTypeOption[] = [];
  gates: Array<{ id: number; label: string }> = [];
  isLoading = signal(false);
  isSaving = signal(false);

  private readonly api = inject(ApiEvPriceRateService);
  private readonly gateApi = inject(ApiParkingMasterDataService);
  private readonly mapper = inject(MapperEvPriceRateService);
  private readonly gateMapper = inject(MapperParkingMasterDataService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);

  get isEdit(): boolean {
    return this.itemId > 0;
  }

  get isTimeMode(): boolean {
    return String(this.formData?.billingMode || '').toLowerCase() === 'time';
  }

  get isEnergyMode(): boolean {
    return String(this.formData?.billingMode || '').toLowerCase() === 'energy';
  }

  ngOnInit(): void {
    this.formData = this.mapper.createEmpty(this.siteId, this.siteName);
    this.loadDropdowns();
    if (this.isEdit) this.loadDetail();
  }

  onClose(): void {
    this.close.emit();
  }

  onConnectorChange(): void {
    const selected = this.connectors.find((c) => c.id === this.formData.evConnectorTypeId);
    if (!selected) return;
    this.formData.connectorCode = selected.code;
    this.formData.connectorName = selected.name;
    if (!this.isEdit) {
      this.mapper.applyConnectorDefaults(this.formData, selected.code);
    }
  }

  onBillingModeChange(mode: 'time' | 'energy'): void {
    this.formData.billingMode = mode;
    if (mode === 'time') {
      if (!this.formData.unitMinute || this.formData.unitMinute <= 0) this.formData.unitMinute = 60;
      this.formData.unitKwh = null;
    } else {
      if (!this.formData.unitKwh || this.formData.unitKwh <= 0) this.formData.unitKwh = 1;
      this.formData.unitMinute = null;
    }
  }

  onSave(): void {
    if (!this.siteId) {
      this.toast.error('กรุณาเลือกลานจาก Sidebar');
      return;
    }
    if (!this.formData.evConnectorTypeId) {
      this.toast.error('กรุณาเลือกชนิดหัวชาร์จ');
      return;
    }
    if (!this.formData.billingMode) {
      this.toast.error('กรุณาเลือกโหมดคิดเงิน');
      return;
    }
    if (!this.formData.unitPrice || this.formData.unitPrice <= 0) {
      this.toast.error('ราคาต่อหน่วยต้องมากกว่า 0');
      return;
    }
    if (this.isTimeMode && (!this.formData.unitMinute || this.formData.unitMinute <= 0)) {
      this.toast.error('กรุณาระบุจำนวนนาทีต่อหน่วย');
      return;
    }
    if (this.isEnergyMode && (!this.formData.unitKwh || this.formData.unitKwh <= 0)) {
      this.toast.error('กรุณาระบุ kWh ต่อหน่วย');
      return;
    }
    if (this.isEdit && !this.permissionService.canUpdate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์แก้ไข');
      return;
    }
    if (!this.isEdit && !this.permissionService.canCreate(this.router.url)) {
      this.toast.error('ไม่มีสิทธิ์สร้าง');
      return;
    }

    const userId = this.resolveUserId();
    const menuId = this.permissionService.getMenuId(this.router.url) ?? 0;
    if (!userId || !menuId) {
      this.toast.error('ไม่พบข้อมูลผู้ใช้หรือสิทธิ์เมนู');
      return;
    }

    this.formData.siteId = this.siteId;
    this.formData.siteName = this.siteName;

    const request$ = this.isEdit
      ? this.api.updateEvPriceRate(this.mapper.mapUpdatePayload(this.formData, userId, menuId))
      : this.api.insertEvPriceRate(this.mapper.mapInsertPayload(this.formData, userId, menuId));

    this.isSaving.set(true);
    request$.subscribe({
      next: (res) => {
        this.isSaving.set(false);
        const mapped = this.mapper.mapActionResponse(res);
        if (mapped.isSuccess) {
          this.toast.success(mapped.message || (this.isEdit ? 'อัปเดตเรียบร้อย' : 'บันทึกเรียบร้อย'));
          this.saveSuccess.emit();
        } else {
          this.toast.error(mapped.message || 'บันทึกไม่สำเร็จ');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.toast.error(err?.error?.message || 'เกิดข้อผิดพลาด');
      },
    });
  }

  private loadDropdowns(): void {
    this.api.getEvConnectorTypes(true).subscribe({
      next: (res) => {
        this.connectors = (res ?? []).map((d) => this.mapper.mapConnector(d));
      },
      error: () => {
        this.connectors = [];
        this.toast.error('โหลดชนิดหัวชาร์จไม่สำเร็จ');
      },
    });

    if (!this.siteId) {
      this.gates = [];
      return;
    }

    this.gateApi
      .getParkingGateForTable({ page: 1, pageSize: 200, siteId: this.siteId })
      .subscribe({
        next: (res) => {
          const mapped = this.gateMapper.mapTable(res, (dto: ParkingGateDto) =>
            this.gateMapper.mapGate(dto)
          );
          this.gates = mapped.data.map((g) => ({
            id: g.id,
            label: g.deviceKey
              ? `${g.name || 'Gate'} (${g.deviceKey})`
              : g.name || `Gate #${g.id}`,
          }));
        },
        error: () => {
          this.gates = [];
        },
      });
  }

  private loadDetail(): void {
    this.isLoading.set(true);
    this.api.getEvPriceRateById(this.itemId).subscribe({
      next: (res) => {
        this.formData = this.mapper.mapRow(res);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายละเอียดไม่สำเร็จ');
        this.onClose();
      },
    });
  }

  private resolveUserId(): number {
    const local = localStorage.getItem('userId');
    if (local) return Number(local);
    return Number(this.cookieService.get('user_id') || 0);
  }

  get canShowActivityHistory(): boolean {
    return this.itemId > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
